import type { WorkerRequest, WorkerResponse } from './crypto.worker.ts';
import type { Argon2Params } from '../crypto/kdf.ts';
import type { FileMetadata } from '../crypto/format.ts';
import type { EncryptProgress } from '../crypto/encrypt.ts';
import type { DecryptProgress } from '../crypto/decrypt.ts';

export type CryptoWorkerClient = {
  encrypt: (opts: {
    password: string;
    plaintext: Uint8Array;
    metadata?: FileMetadata;
    chunkSize?: number;
    argon2?: Argon2Params;
    onProgress?: (p: EncryptProgress | DecryptProgress) => void;
  }) => Promise<Uint8Array>;
  encryptFile: (opts: {
    password: string;
    file: File;
    metadata?: FileMetadata;
    chunkSize?: number;
    argon2?: Argon2Params;
    onProgress?: (p: EncryptProgress | DecryptProgress) => void;
  }) => Promise<Uint8Array>;
  decrypt: (opts: {
    password: string;
    container: Uint8Array;
    onProgress?: (p: EncryptProgress | DecryptProgress) => void;
  }) => Promise<{ plaintext: Uint8Array; metadata: FileMetadata }>;
  encryptText: (opts: {
    password: string;
    text: string;
    argon2?: Argon2Params;
    chunkSize?: number;
  }) => Promise<string>;
  decryptText: (opts: { password: string; payload: string }) => Promise<string>;
  cancel: () => void;
  terminate: () => void;
};

function transferCopy(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

export function createCryptoWorker(): CryptoWorkerClient {
  const worker = new Worker(new URL('./crypto.worker.ts', import.meta.url), {
    type: 'module',
  });

  let seq = 0;
  let lastId: string | null = null;
  const pending = new Map<
    string,
    {
      resolve: (v: unknown) => void;
      reject: (e: Error) => void;
      onProgress?: (p: EncryptProgress | DecryptProgress) => void;
    }
  >();

  worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
    const msg = ev.data;
    const entry = pending.get(msg.id);
    if (!entry) return;

    if (msg.type === 'progress') {
      entry.onProgress?.(msg.progress);
      return;
    }
    if (msg.type === 'error') {
      pending.delete(msg.id);
      const err = new Error(msg.message);
      err.name = msg.name;
      entry.reject(err);
      return;
    }
    pending.delete(msg.id);
    if (msg.type === 'encrypt-done') {
      entry.resolve(new Uint8Array(msg.container));
    } else if (msg.type === 'decrypt-done') {
      entry.resolve({
        plaintext: new Uint8Array(msg.plaintext),
        metadata: msg.metadata,
      });
    } else if (msg.type === 'encrypt-text-done') {
      entry.resolve(msg.payload);
    } else if (msg.type === 'decrypt-text-done') {
      entry.resolve(msg.text);
    }
  };

  worker.onerror = (ev) => {
    for (const [id, entry] of pending) {
      pending.delete(id);
      entry.reject(new Error(ev.message || 'Worker error'));
    }
  };

  function request<T>(
    payload: WorkerRequest,
    onProgress?: (p: EncryptProgress | DecryptProgress) => void,
    transfer?: ArrayBuffer[],
  ): Promise<T> {
    lastId = payload.id;
    return new Promise<T>((resolve, reject) => {
      pending.set(payload.id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        onProgress,
      });
      worker.postMessage(payload, transfer ?? []);
    });
  }

  return {
    encrypt: (opts) => {
      const id = `enc-${++seq}`;
      const buf = transferCopy(opts.plaintext);
      return request(
        {
          id,
          type: 'encrypt',
          password: opts.password,
          plaintext: buf,
          metadata: opts.metadata,
          chunkSize: opts.chunkSize,
          argon2: opts.argon2,
        },
        opts.onProgress,
        [buf],
      );
    },
    encryptFile: (opts) => {
      const id = `enf-${++seq}`;
      return request(
        {
          id,
          type: 'encrypt-file',
          password: opts.password,
          file: opts.file,
          metadata: opts.metadata,
          chunkSize: opts.chunkSize,
          argon2: opts.argon2,
        },
        opts.onProgress,
      );
    },
    decrypt: (opts) => {
      const id = `dec-${++seq}`;
      const buf = transferCopy(opts.container);
      return request(
        {
          id,
          type: 'decrypt',
          password: opts.password,
          container: buf,
        },
        opts.onProgress,
        [buf],
      );
    },
    encryptText: (opts) => {
      const id = `et-${++seq}`;
      return request({
        id,
        type: 'encrypt-text',
        password: opts.password,
        text: opts.text,
        argon2: opts.argon2,
        chunkSize: opts.chunkSize,
      });
    },
    decryptText: (opts) => {
      const id = `dt-${++seq}`;
      return request({
        id,
        type: 'decrypt-text',
        password: opts.password,
        payload: opts.payload,
      });
    },
    cancel: () => {
      if (lastId) {
        worker.postMessage({ id: lastId, type: 'cancel' } satisfies WorkerRequest);
      }
    },
    terminate: () => worker.terminate(),
  };
}
